import React, { useContext, useEffect, useRef } from 'react'
import { StyleSheet, Text, View, TouchableOpacity, Platform, Linking, Share } from 'react-native'
import { textStyles, theme } from '../style'
import { Company, Dictionary, Value } from '@ecountabl/lib'
import { FontAwesome, FontAwesome5, Ionicons } from "@expo/vector-icons"
import * as Clipboard from 'expo-clipboard'
import { usePopover, showPopoverFromRef } from '../utility/popover'
import { ThemeContext } from '../utility/context/ThemeContext'
import Badge from '../utility/badge'
import RNWebView from 'react-native-webview'
import { CustomActionSheet, CustomActionSheetRef } from '../utility/CustomActionSheet'
import { StackActions, useNavigation } from '@react-navigation/native'
import { useAppSelector } from '../store'
import Haptics from "../utility/haptics"
import Analytics from '../utility/analytics'

type Props = {
  company: Company
  values: { map: Dictionary<Value> }
  fetchingUserScores?: boolean
}

export const Header: React.FC<Props> = ({ company }: Props) => {
  const navigation = useNavigation()
  const { user } = useAppSelector(s => s.auth)
  const theme = useContext(ThemeContext)
  const { primary, second, white, textMain, bgSecond, borderColor } = theme.theme.colors

  const { showFromAnchor, hide } = usePopover()
  const websiteAnchorRef = useRef<View>(null)
  const sheetRef = useRef<CustomActionSheetRef>(null)
  const hidePopoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (hidePopoverTimeoutRef.current != null) {
        clearTimeout(hidePopoverTimeoutRef.current)
      }
    }
  }, [])

  const {
    website,
    // dataCompleteness,
    corporate_parent,
    corporate_parent_id,
    segment,
    name
  } = company

  const navigateToParentBrand = () => {
    Haptics.selection()
    Analytics.track('company_clicked', {
      companyId: corporate_parent_id?.toString() ?? '',
      companyName: corporate_parent,
      source: "parent_brand",
    })

    navigation.dispatch(
      StackActions.push('Company', {
        id: corporate_parent_id?.toString() ?? undefined,
        name: corporate_parent,
        source: "parent_brand",
      })
    )
  }
  
  

  let companySiteURL: URL
  if (website) {
    try {
      companySiteURL = new URL(website)
    } catch (e) {
      companySiteURL = new URL(`https://${website}`)
    }
  }

  const showCopyWebsitePopover = (success: boolean | null) => {
    // if copy successful on web, success is true
    // on mobile, success stays null no matter what
    const message = (success == null || success)
      ? 'Link copied to clipboard'
      : 'Failed to copy link to clipboard'

    const contentNode = (
      <Text style={[textStyles.body, { color: textMain }]}>
        {message}
      </Text>
    )

    showPopoverFromRef(
      websiteAnchorRef,
      showFromAnchor,
      contentNode,
      undefined,
      {
        offset: { x: 0, y: 8 },
        appearance: { backgroundColor: bgSecond, borderColor: borderColor + '20', borderWidth: 1, borderRadius: 6 },
        align: 'center',
        showArrow: true
      }
    )

    if (hidePopoverTimeoutRef.current != null) {
      clearTimeout(hidePopoverTimeoutRef.current)
    }

    hidePopoverTimeoutRef.current = setTimeout(() => {
      hidePopoverTimeoutRef.current = null
      hide()
    }, 3000)
  }

  const copyWebsiteURL = async () => {
    try {
      const success = await Clipboard.setStringAsync(companySiteURL.href)
      showCopyWebsitePopover(success)
    } catch {
      showCopyWebsitePopover(false)
    }
  }
  
  const handleWebsitePress = () => {
    if (Platform.OS == "web") {
      void copyWebsiteURL()
    } else {
      // launch webview in mobile app
      sheetRef.current.show()
    }
  }

  // const valueCount = Object.keys(values?.map).length
  const showParent = corporate_parent !== null && corporate_parent !== name

  return (
    <View style={{ flex: 1, minWidth: 250 }}>
      <Text style={[textStyles.h2, {color: textMain}]}>{name}</Text>
      <View>
        <Text style={[textStyles.body, {marginBottom: 5, color: textMain}]}>
          Category: {segment}
        </Text>
        {showParent && !!corporate_parent && (
          <Text style={{ marginBottom: 5, color: textMain }}>
            <Text style={textStyles.body}>Parent Brand: </Text>
            {
              corporate_parent_id ? (
                <Text
                  style={[
                    textStyles.body,
                    styles.parent_link,
                  ]}
                  onPress={navigateToParentBrand}
                >
                  {corporate_parent}
                </Text>
              ) : <Text
                  style={[textStyles.body, {flexWrap: "wrap"}]}
                >
                  {corporate_parent}
                </Text>
            }
          </Text>
        )}
        {
          company.state ?
          <>
            <Text style={[textStyles.body, {marginBottom: 5, color: textMain}]}>
              HQ: {company.city ? `${company.city}, ` : null}{company.state}
            </Text>
            {
              user?.state == company.state ?
              <Text style={[textStyles.body, {marginBottom: 5, color: textMain}]}>
                <Badge
                  icon={<Ionicons name="location-sharp" size={16} color={white}/>}
                  style={{
                    backgroundColor: primary,
                    marginRight: 5
                  }}
                />
                {user.state}-based Business
              </Text>
              
              : null
            }
          </>
          
          : null
        }
        {
          companySiteURL ? (
            <View style={{ alignSelf: "flex-start"}}>
              <TouchableOpacity
                ref={websiteAnchorRef}
                style={styles.websitePressable}
                onPress={() => {
                  Haptics.selection()
                  handleWebsitePress()
                }}
              >
                <Text
                  style={[textStyles.body, styles.parent_link, {color: Platform.OS != "web" ? primary : textMain}]}
                  numberOfLines={1}
                  ellipsizeMode='tail'
                >
                  {companySiteURL.hostname}
                </Text>
                {
                  Platform.OS == "web" && (
                    <FontAwesome5
                      name={"clipboard"}
                      size={16}
                      color={second}
                    />
                  )
                }
              </TouchableOpacity>
            </View>
          ) : null
        }
      </View>
      {
        Platform.OS !== "web" && companySiteURL && (
          <CustomActionSheet
            ref={sheetRef}
            name="company-website"
            // Android: allow the WebView to capture scroll gestures instead of the sheet.
            enableContentPanningGesture={Platform.OS !== 'android'}
            menuContent={
              Linking.canOpenURL(companySiteURL.href) ? (
                <View>
                  <TouchableOpacity onPress={() => {
                    Haptics.selection()
                    Linking.openURL(companySiteURL.href)
                  }}>
                    <View style={{flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, marginVertical: 8}}>
                      <FontAwesome name='external-link' color={textMain} size={16}/>
                      <Text style={[textStyles.body, {color: textMain}]}>Open in Browser</Text>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => {
                    Haptics.selection()
                    Share.share({url: companySiteURL.href})
                  }}>
                    <View style={{flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, marginVertical: 8}}>
                      <FontAwesome name='share' color={textMain} size={16}/>
                      <Text style={[textStyles.body, {color: textMain}]}>Share</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              ) : null
            }
          >
            <View style={{height: "100%"}}>
              <RNWebView
                source={{ uri: companySiteURL.href }}
                // Android: allow nested scrolling inside gesture-handler containers.
                nestedScrollEnabled={Platform.OS === 'android'}
                style={{height: "100%", width: "100%"}}
              />
            </View>
          </CustomActionSheet>
        )
      }
    </View>
  )
}

const styles = StyleSheet.create({
  parent_link: {
    color: theme.colors.second,
    // textDecorationLine: 'underline',
    flexWrap: 'wrap'
  },
  websitePressable: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  }
})

export default Header
